/**
 * The shapes the dashboard reads — **authored once, and validated on the way in.**
 *
 * ## Why these exist rather than tRPC's inferred types
 *
 * R7 delivers end-to-end types by having the client import the server's router type.
 * c33 tried that and put it back, because the type edge `apps/app -> apps/api ->
 * packages/db` trips `spa-has-no-server-packages-reachable` — the rule whose own comment
 * calls it *"the clearest demonstration in this file that reachability buys coverage no
 * direct rule provides"* — and dependency-cruiser cannot express an exemption for one
 * intermediate on a `reachable` rule. The choice was the typed client or that rule, and
 * the rule won.
 *
 * **This is one definition per shape either way**, which is what the ruling actually
 * requires: the ops layer's return types are `z.infer` of the schemas below, and the SPA
 * parses responses with the same schemas. Nothing is transcribed. What is lost is
 * inference across the wire; what is gained is that the shape is **checked at runtime**
 * rather than asserted at compile time — so a server that starts answering something else
 * is caught by the client instead of being cast into a type that no longer describes it.
 *
 * The residue, stated rather than hidden: **procedure paths** (`recalls.list`) are
 * strings in `apps/app/src/api/`, and nothing compile-checks them. A wrong path is a
 * `NOT_FOUND` from tRPC on the first run — loud, immediate, and not a class of mistake
 * that survives to production.
 *
 * ## The snake_case is deliberate and is not a style lapse
 *
 * `products.list` and `companyPrefixes.list` return raw column names because that is what
 * `GET /v1/products` and `GET /v1/company-prefixes` have returned since c28 and c29, and
 * **the REST wire may not move** — it is a paid machine-client surface. c34's list rebuild
 * is where those responses are redesigned deliberately, with a spec amendment; a refactor
 * is not.
 */
import { z } from "zod";
import { DOMAIN_KINDS, DOMAIN_STATUSES } from "../serving.js";
import { BATCH_STATUSES, MEDIA_KINDS, PRODUCT_STATUSES } from "../catalog.js";
import { GATE_REMEDY_ACTIONS, } from "../gate.js";
import { RECALL_SCOPES, RECALL_STATUSES } from "../recalls.js";
import { BILLING_STATES, TIERS } from "../tiers.js";
import { VERIFICATIONS } from "../verification.js";
import { ARTWORK_WATERMARKS } from "./artwork.js";
import { ARTWORK_BATCH_STATUSES } from "../artwork-batch.js";
import { membershipSummarySchema } from "./orgs.js";
/** `orgs.mine` — the query that decides what the org header may be set to. */
export const myOrgsSchema = z.object({
    orgs: z.array(membershipSummarySchema),
});
/**
 * `orgs.current` — the membership, plus where this organization's codes answer.
 *
 * `resolverBaseUrl` is configuration the browser cannot read for itself, handed over as
 * data so that *"View live page"* opens **exactly what a scan gets**. The path half comes
 * from `digitalLinkPath` in `packages/shared`, which is the same function the API's own URL
 * builder calls — one spelling of `/01/`, two processes.
 *
 * A `VITE_RESOLVER_BASE_URL` would have been the obvious alternative and is the one this
 * exists to avoid: two readers of one configured value, where the value that drifts is the
 * one printed on a carton.
 */
export const workspaceSummarySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
});
export const currentOrgSchema = membershipSummarySchema.extend({
    resolverBaseUrl: z.url(),
    /**
     * Every workspace in the organization, oldest first — **the set of legal values, not a
     * choice made for the caller.**
     *
     * `POST /v1/products` requires a `workspaceId` and nothing exposed one until c34. A
     * singular field was the obvious fix and is wrong: `workspaces` has no `is_default`
     * column, so with several rows there is no defensible singular answer, and picking one
     * would file a product in a workspace nobody chose. This is the same shape and the same
     * reasoning as `orgs.mine` — the query that decides what a form may set.
     */
    workspaces: z.array(workspaceSummarySchema),
    plan: z.enum(TIERS),
    billingStatus: z.enum(BILLING_STATES),
    // The ladder's facts, for the banners and the billing screen's states — `ladder.ts`.
    graceSince: z.iso.datetime().nullable(),
    paymentFailure: z.string().nullable(),
    fairUseOver: z.boolean(),
    // Null for an organisation created before `0035` — `trial.ts`.
    trialEndsAt: z.iso.datetime().nullable(),
    // What the trial is used for, counted while it runs and null otherwise.
    trialUsage: z
        .object({
        batchLevelLinks: z.number().int().nonnegative(),
        extraLanguages: z.number().int().nonnegative(),
    })
        .nullable(),
    // `organizations.settings.hardCap`, false when the key is absent.
    hardCap: z.boolean(),
    // `PLANS[plan]`'s capacities, derived by the API — `readCurrentOrg`, plans.ts.
    limits: z.object({
        activeGtins: z.number().int(),
        pooledGtins: z.boolean(),
        fairUsePerMonth: z.number().int().nullable(),
    }),
});
/**
 * One recall, as `GET /v1/recalls` presents it.
 *
 * `restore` is **absent**, and that is F1 rather than an omission: no response carries
 * which links a recall suppressed, and c35 is the commit that adds `restore.suppressed`
 * to the recall *detail* — with the screen that consumes it. Adding it here would ship an
 * API change into a commit with nothing to read it.
 */
export const recallSummarySchema = z.object({
    id: z.uuid(),
    gtin14: z.string(),
    productId: z.uuid(),
    scope: z.enum(RECALL_SCOPES),
    lots: z.array(z.string()),
    status: z.enum(RECALL_STATUSES),
    overrideDefault: z.boolean(),
    externalUrl: z.string().nullable(),
    publishedAt: z.string(),
    liftedAt: z.string().nullable(),
});
export const recallListSchema = z.object({ recalls: z.array(recallSummarySchema) });
/** One filed document, as the claimant may see it. No `storage_key`: a key is ours, not theirs. */
export const prefixDocumentSummarySchema = z.object({
    id: z.uuid(),
    kind: z.string(),
    filename: z.string().nullable(),
    submitted_at: z.string(),
});
/** One prefix, in the trust spine's own vocabulary. See the header on snake_case. */
export const companyPrefixSummarySchema = z.object({
    id: z.uuid(),
    prefix: z.string(),
    gs1_mo: z.string().nullable(),
    verification: z.enum(VERIFICATIONS),
    verified_at: z.string().nullable(),
    /** Oldest first. The timeline reads `submitted_at` off the newest. */
    documents: z.array(prefixDocumentSummarySchema),
});
export const companyPrefixListSchema = z.object({
    prefixes: z.array(companyPrefixSummarySchema),
});
/**
 * One product, as the list returns it today.
 *
 * `name` is `unknown` because it is a jsonb map of language tag to string and **the
 * dashboard has no business flattening it here** — per-field language switching is c34's,
 * at the field and never page-wide, and a schema that picked one language would be that
 * decision made in the wrong place.
 */
export const productSummarySchema = z.object({
    id: z.uuid(),
    gtin14: z.string(),
    sku: z.string().nullable(),
    brand_name: z.string(),
    name: z.unknown(),
    status: z.enum(PRODUCT_STATUSES),
    published_at: z.string().nullable(),
    /**
     * When the row was created — **not nullable, unlike `published_at`**, and that
     * difference is the whole reason the list's date column shows this one.
     *
     * The column read "Published", rendered `published_at`, and sorted `createdAt`:
     * three legs naming three different fields. `createdAt` is the only date in
     * `PRODUCT_SORT_COLUMNS`, and it is the sensible one to sort by — every row has
     * one, where a draft has no publish date at all and a keyset over a nullable
     * column drops rows rather than ordering them. So the column follows the sort
     * rather than the other way round, and this field is what it needs to render.
     */
    created_at: z.string(),
});
/** A list row: the batch lot `?q=` matched exactly, or null — plan 3R §4 U52. */
export const productListRowSchema = productSummarySchema.extend({
    matched_lot: z.string().nullable(),
});
export const productListSchema = z.object({
    products: z.array(productListRowSchema),
    /**
     * How many rows match the **filter** — not how many are in this page.
     *
     * Optional because the REST wire carried no `total` before c34 and a client reading an
     * older response is not wrong; the SPA treats its absence as "unknown" rather than as
     * zero, which is the difference between a select-all bar that waits and one that lies.
     */
    total: z.number().int().nonnegative().optional(),
    /** Present on the tRPC projection; REST carries the same value in `X-Next-Cursor`. */
    nextCursor: z.string().nullable().optional(),
});
/**
 * `products.counts` — catalogue totals per status.
 *
 * **A separate call rather than a field on the list, because it answers a different
 * question.** The dashboard's panel is about the whole catalogue; the list is about a
 * page of it. Deriving one from the other is exactly the defect c34 introduced by
 * paginating the list and then had to come back and fix.
 *
 * Keyed by status rather than four named fields so that adding a status to
 * `PRODUCT_STATUSES` does not require a schema change here — and read with a `?? 0` at
 * the call site, because a status nobody has used is absent rather than zero.
 *
 * **`partialRecord`, and the distinction is the whole defect.** In Zod 4 a `z.record`
 * keyed by an enum is **exhaustive** — every member of the enum is required, so
 * `{ counts: {} }` and `{ counts: { published: 1 } }` are both rejected. This line read
 * `z.record` from c34 until c39, while the paragraph above it, the op that produces the
 * value (`apps/api/src/ops/product-list.ts` — *"A status with no rows is absent from the
 * result and is filled in as zero by the caller"*) and the consumer's own
 * `Readonly<Partial<Record<string, number>>>` all described partial semantics. Three
 * components agreed and the combinator dissented, so **`GET /` failed for every
 * organization that did not hold a product in all four statuses** — which is every new
 * one. `partialRecord` is the partial spelling and still rejects an unknown key with
 * `invalid_key`, which is why it beats `z.record(z.string(), …)`: a renamed or miscased
 * status must stay loud rather than parse and render as zero through the `?? 0`.
 *
 * The closed key set is deliberate, and consistent with `productSummarySchema.status`
 * three schemas up: a product status is a state machine with a SQL CHECK, ops edges and
 * resolver behaviour behind it, added as an architectural act. That is the opposite of
 * the audit `action` vocabulary, which is `z.string()` precisely because actions are
 * appended routinely. A stale bundle meeting a genuinely new fifth status refuses the
 * response, and refusing is the right answer for this field.
 */
export const productCountsSchema = z.object({
    counts: z.partialRecord(z.enum(PRODUCT_STATUSES), z.number().int().nonnegative()),
});
/**
 * What `products.publish` and `products.retire` both answer with.
 *
 * Two fields, and the second is the one worth having: the status comes back **from the
 * server** rather than being assumed by the caller. Publishing is not guaranteed to land
 * `published` — the op has a branch where the GTIN turns out to be claimed by another
 * organization and one where no default link holds the slot, and both refuse. A client
 * that assumed the outcome would render a published product over a refusal it had not
 * read.
 *
 * One schema for two procedures because they answer the same shape, not because they are
 * the same act: retiring stops at admin and publishing does not, and that difference lives
 * in `authorize.ts` where it belongs rather than in two response types that differ only by
 * name.
 */
export const productStatusSchema = z.object({
    id: z.uuid(),
    status: z.enum(PRODUCT_STATUSES),
});
/**
 * `products.create` — what the op answers with, and it is not `productSummarySchema`.
 *
 * The create op returns `brandName` in camelCase while the list returns `brand_name`,
 * because the list is the REST wire shipped since c28 and **that wire may not move** while
 * the create response is the op's own. Two schemas rather than one lenient schema that
 * accepts either spelling: a client that accepts both cannot tell a version skew from a
 * successful call, which is the distinction `ResponseShapeError` exists to preserve.
 *
 * `status` is here and is always `draft`. A product is created draft, always — publishing is
 * a separate act with its own permission and its own side effects — so this field is a fact
 * the client can rely on rather than a value it has to interpret.
 */
export const productCreatedSchema = z.object({
    id: z.uuid(),
    gtin14: z.string(),
    sku: z.string().nullable(),
    brandName: z.string(),
    status: z.enum(PRODUCT_STATUSES),
});
/**
 * The detail screen's three tabs, in the shapes their ops already return.
 *
 * Snake_case in `media` for the same reason the list carries it: these are the columns
 * `GET /v1/media` has returned since c28, and **the REST wire may not move.** The tRPC
 * projection carries the same body, so renaming here would fork one definition into two
 * spellings — which is exactly what R7 forbids.
 */
export const variantSummarySchema = z.object({
    id: z.uuid(),
    cpv: z.string(),
    /** The language map, unflattened. See {@link productSummarySchema}'s note on `name`. */
    name: z.unknown(),
});
export const variantListSchema = z.object({ variants: z.array(variantSummarySchema) });
export const batchSummarySchema = z.object({
    id: z.uuid(),
    lot: z.string(),
    /** `YYYY-MM-DD`, fixed — `screens.md` §4. Null for a batch with no expiry. */
    expiry: z.string().nullable(),
    status: z.enum(BATCH_STATUSES),
});
export const batchListSchema = z.object({ batches: z.array(batchSummarySchema) });
export const mediaSummarySchema = z.object({
    id: z.uuid(),
    product_id: z.uuid().nullable(),
    kind: z.enum(MEDIA_KINDS),
    storage_key: z.string(),
    mime: z.string(),
    position: z.number().int(),
});
export const mediaListSchema = z.object({ media: z.array(mediaSummarySchema) });
/**
 * One link, as `presentLink` returns it.
 *
 * **`grain` omits its absent qualifiers rather than nulling them**, which is the shape the
 * REST wire has carried since c30 and is therefore not this schema's to tidy. It reads
 * oddly beside `previewLinkSchema`, which nulls them — and the difference is real: the
 * preview's grain is *computed* and always has three answers, while a link's grain is the
 * row's own and "absent" is what makes it inheritable.
 */
export const linkSummarySchema = z.object({
    id: z.uuid(),
    gtin14: z.string(),
    productId: z.uuid(),
    grain: z.object({
        cpv: z.string().optional(),
        lot: z.string().optional(),
        serial: z.string().optional(),
    }),
    linkType: z.string(),
    href: z.string(),
    title: z.string().nullable(),
    hreflang: z.array(z.string()).nullable(),
    mediaType: z.string().nullable(),
    isDefault: z.boolean(),
    active: z.boolean(),
    validFrom: z.string().nullable(),
    validTo: z.string().nullable(),
    position: z.number().int(),
    /** Non-null means the recall flow created this row, and §3.8 locks it while active. */
    recallId: z.uuid().nullable(),
});
export const linkListSchema = z.object({ links: z.array(linkSummarySchema) });
/**
 * What `links.create` and `links.update` answer with — **the op's shape, not the REST wire's.**
 *
 * REST spreads the warnings onto the link (`{...link, warnings}`), because that is what
 * `POST /v1/products/{id}/links` has answered since c30 and the wire may not move. tRPC
 * returns the op's own `{created, warnings}` unflattened. One definition, two projections —
 * R7's seam, and the reason this schema is not `linkSummarySchema.extend({warnings})`.
 */
export const warningBodySchema = z.object({
    code: z.string(),
    title: z.string(),
    detail: z.string(),
});
export const linkCreatedSchema = z.object({
    created: linkSummarySchema,
    warnings: z.array(warningBodySchema),
});
export const linkUpdatedSchema = z.object({
    updated: linkSummarySchema,
    warnings: z.array(warningBodySchema),
});
/** What `recalls.publish` answers. `warnings` carries `RECALL_NOT_RESOLVING` on a draft product. */
export const recallPublishedSchema = z.object({
    recall: recallSummarySchema,
    warnings: z.array(warningBodySchema),
});
/** What `recalls.widen` answers. `widened: false` means every lot named was already covered. */
export const recallWidenedSchema = z.object({
    recall: recallSummarySchema,
    widened: z.boolean(),
    warnings: z.array(warningBodySchema),
});
/** What `recalls.lift` answers — what the lift did, not what the recall held. */
export const recallLiftedSchema = z.object({
    recall: recallSummarySchema,
    restore: z.object({
        reinstated: z.array(z.string()),
        skipped: z.array(z.object({ linkId: z.string(), reason: z.enum(["gone", "slot-taken"]) })),
        linksDeleted: z.number().int(),
    }),
    warnings: z.array(warningBodySchema),
});
export const linkActiveSchema = z.object({
    id: z.uuid(),
    active: z.boolean(),
    /** False when the link was already in the requested state — a no-op does not emit. */
    changed: z.boolean(),
});
export const linkDeletedSchema = z.object({ id: z.uuid(), deleted: z.literal(true) });
export const linkOrderSchema = z.object({
    gtin14: z.string(),
    order: z.array(z.string()),
});
/**
 * One link type from the GS1 Web Vocabulary snapshot.
 *
 * `isDefaultEligible` is the field c35's type picker exists to read: it is false for
 * exactly the three types GS1 restricts to the resolver, and offering one of those as a
 * default would produce a `LINK_TYPE_NOT_ASSIGNABLE` the user could have been spared.
 * **Read rather than hardcoded** — the snapshot is refreshed by release, and a screen with
 * its own list of the three would be a fourth copy of a fact the registry owns.
 */
export const linkTypeSummarySchema = z.object({
    curie: z.string(),
    title: z.string(),
    description: z.string(),
    status: z.string(),
    restricted: z.array(z.string()),
    isDefaultEligible: z.boolean(),
    source: z.string(),
});
export const linkTypeListSchema = z.object({
    linkTypes: z.array(linkTypeSummarySchema),
});
/**
 * One entry in `recalls.restore.suppressed` — a brand default this recall cleared.
 *
 * §3.8's shape, and F1's fix: until c35 no response carried it, so link management could
 * only show a user's own default silently unflagged. `grain` nulls its absent qualifiers
 * here because that is what `sweep` captured off the row's columns.
 */
export const suppressedLinkSchema = z.object({
    linkId: z.uuid(),
    gtin14: z.string(),
    grain: z.object({
        cpv: z.string().nullable(),
        lot: z.string().nullable(),
        serial: z.string().nullable(),
    }),
    hreflang: z.array(z.string()).nullable(),
    mediaType: z.string().nullable(),
    wasDefault: z.boolean(),
    capturedAt: z.string(),
});
/**
 * Who did it — a human, a machine credential, or neither.
 *
 * All four fields are nullable and that is the honest shape rather than a looseness.
 * `actor_user_id` and `actor_api_key_id` are independently nullable columns (§4), so
 * "written by a session", "written by an API key" and "written by neither" are three real
 * states; `email` and `name` come from a join that can miss, because `users.id` is
 * `ON DELETE SET NULL` on this table — **a deleted user leaves their rows behind with no
 * actor**, which is what an append-only trail is supposed to do.
 *
 * `name` is nullable in `users` itself. A screen that needs one word for the actor falls
 * back email → "a deleted user" → "an API key", and that ladder is the screen's decision,
 * not this schema's.
 */
export const auditActorSchema = z.object({
    userId: z.string().nullable(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    apiKeyId: z.string().nullable(),
});
/**
 * One recall with its whole trail and what it is holding.
 *
 * `restore.suppressed` is on the **detail** and deliberately not on
 * {@link recallSummarySchema}: a whole-GTIN recall over a multi-language, multi-variant
 * catalogue suppresses every brand default, and nothing bounds that list — so putting it
 * on the dashboard's attention stack would grow that response by an unbounded amount to
 * serve a screen that does not read it.
 *
 * `version` distinguishes a recall published **before** suppression existed (`{}`, which is
 * `0010`'s column default) from one that suppressed nothing (`suppressed: []`). Those are
 * different facts and a screen that conflated them would say "nothing was suspended" about
 * a recall nobody can answer that question for.
 *
 * **`suppressed` is what this recall captured, not what is suppressed right now.** It
 * survives the lift — measured — because `recalls.restore` is the only structured record of
 * what a recall held at its widest and a lift that erased it would delete that on the way
 * out. **A consumer reads `status` first**, and what each entry's fate was is the
 * `restore_reinstated` event's `reinstated` / `skipped` arrays. See `ops/recalls.ts` for the
 * argument; this is the same shape as `status` versus `published_at` one field over.
 */
export const recallDetailSchema = recallSummarySchema.extend({
    /** Scans the resolver stamped with this recall's id — `drizzle/0029`, plan 3R U50. */
    reach: z.object({
        scans: z.number().int(),
        lastAt: z.string().nullable(),
        byCountry: z.array(z.object({ country: z.string().nullable(), scans: z.number().int() })),
    }),
    restore: z.object({
        version: z.number().int(),
        suppressed: z.array(suppressedLinkSchema),
    }),
    events: z.array(z.object({
        id: z.uuid(),
        at: z.string(),
        kind: z.string(),
        payload: z.unknown(),
        actorUserId: z.string().nullable(),
        actor: auditActorSchema,
    })),
});
/**
 * One row of the organization's trail.
 *
 * **`action` is `z.string()` and not `z.enum(AUDIT_ACTIONS)`, deliberately, and the
 * reasoning is worth keeping because the enum is the tempting choice.** The enum would
 * give the SPA an exhaustive union for its labels, and `0014`'s CHECK now guarantees
 * every stored value is in the list — so it would be *correct today*.
 *
 * It would also mean that the first time a later phase adds an action, any dashboard
 * running the previous bundle **fails to parse the whole page** rather than showing one
 * unfamiliar word. §10.4 rolls migrations ahead of app containers by design, so that skew
 * is the normal state during a deploy rather than an exotic one. An audit log that goes
 * blank because it contains a row the reader has not heard of is the worst failure this
 * screen has available.
 *
 * So the wire is lenient and the *rendering* is exhaustive: `isAuditAction` narrows, and
 * the screen falls back to the raw token. The CHECK stops bad data going in; leniency here
 * is about reading across versions, and the two are different jobs.
 *
 * `before` and `after` are `unknown` because they are — arbitrary jsonb snapshots of
 * whatever the acted-on row was. The screen renders them as a diff and must not assume a
 * shape it cannot check.
 */
export const auditLogEntrySchema = z.object({
    id: z.string(),
    at: z.string(),
    action: z.string(),
    entity: z.string(),
    entityId: z.string().nullable(),
    actor: auditActorSchema,
    before: z.unknown(),
    after: z.unknown(),
});
export const auditLogListSchema = z.object({
    entries: z.array(auditLogEntrySchema),
    /** Present on the tRPC projection; REST carries the same value in `X-Next-Cursor`. */
    nextCursor: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// §8.1's gates, as shapes the dashboard parses — c92
// ---------------------------------------------------------------------------
/**
 * §8.3's disclosure, three fields, exactly as {@link EscapeHatchDisclosure} declares them.
 *
 * The strings themselves are `artwork-copy.ts`'s and are **never** re-authored on the
 * reading side. This schema says what shape arrives; that module says what it says.
 */
export const escapeHatchDisclosureSchema = z.object({
    reassurance: z.string(),
    permanence: z.string(),
    alternative: z.string(),
});
/** §8.3's offer: the host that will print, the URI it would encode, and the disclosure. */
export const platformUriOfferSchema = z.object({
    platformHost: z.string(),
    digitalLinkUri: z.string(),
    disclosure: escapeHatchDisclosureSchema,
});
/**
 * Each remedial act's occurrence data, keyed by the act — mirroring {@link Remedy}'s arms.
 *
 * `satisfies Record<RemedyAction, …>` closes both directions of the census: an act with no
 * arm here fails to compile, and an arm for an act {@link REMEDY_ACTIONS} has never heard
 * of fails at the key. Seven arms, because there are seven acts.
 */
const REMEDY_ARMS = {
    none: z.object({ action: z.literal("none"), label: z.string() }),
    wait_for_domain: z.object({
        action: z.literal("wait_for_domain"),
        label: z.string(),
        hostname: z.string(),
        escapeHatch: platformUriOfferSchema,
    }),
    verify_prefix: z.object({
        action: z.literal("verify_prefix"),
        label: z.string(),
        prefix: z.string().optional(),
    }),
    await_prefix_review: z.object({
        action: z.literal("await_prefix_review"),
        label: z.string(),
        prefix: z.string(),
    }),
    raise_x_dimension: z.object({
        action: z.literal("raise_x_dimension"),
        label: z.string(),
        preset: z.string(),
        requestedXDimensionMm: z.number(),
        minimumXDimensionMm: z.number(),
        targetXDimensionMm: z.number(),
        derivation: z.string(),
    }),
    publish_product: z.object({
        action: z.literal("publish_product"),
        label: z.string(),
        productId: z.string(),
    }),
    restore_product: z.object({
        action: z.literal("restore_product"),
        label: z.string(),
        productId: z.string(),
    }),
};
/**
 * One remedy, whichever act it carries. The reading side of {@link Remedy}.
 *
 * ## Why a second spelling of a type this package already declares is allowed here
 *
 * `problem.ts`'s header states the hazard plainly: *"two fields that must agree and are
 * typed independently will disagree."* This is such a pair — `gate.ts` declares the type
 * an op builds, and this declares the shape the dashboard parses — so the two are **not**
 * typed independently. {@link GateSchemaWidensToType} and {@link GateTypeWidensToSchema}
 * below are compile-time assertions in both directions, which turn a divergence into a
 * `tsc` failure at this file rather than into a `ResponseShapeError` on a screen.
 *
 * The alternative — deriving `Remedy` from this schema — was refused for the reason the
 * SPA cannot import the router type: `gate.ts` is the **contract** module, read by anyone
 * tracing what a refusal means, and it stays readable by holding types rather than a
 * validator. `gateRefusal`'s generic pairing of `code` to `remedy` arm is also expressed
 * in the type system and has no zod equivalent that keeps the same call-site error.
 */
export const remedySchema = z.discriminatedUnion("action", [
    REMEDY_ARMS.none,
    REMEDY_ARMS.wait_for_domain,
    REMEDY_ARMS.verify_prefix,
    REMEDY_ARMS.await_prefix_review,
    REMEDY_ARMS.raise_x_dimension,
    REMEDY_ARMS.publish_product,
    REMEDY_ARMS.restore_product,
]);
/** Every member RFC 9457 requires, shared by the eight arms below. */
const problemMembers = {
    type: z.string(),
    title: z.string(),
    status: z.number(),
    detail: z.string(),
};
/**
 * One gate code's body — and **the pairing of code to remedy arm is read from
 * {@link GATE_REMEDY_ACTIONS}, never restated here.**
 *
 * That map is the single place §8.1's code↔act pairing is written, and `RemedyFor<C>` on
 * the emitting side is a lookup into it. This is the same lookup on the reading side: the
 * generic parameter makes `GATE_REMEDY_ACTIONS[C]` a literal act, which indexes
 * {@link REMEDY_ARMS} to exactly one arm. A schema that hand-paired them would be the
 * second list `gate.ts`'s own header refuses.
 *
 * The consequence is real rather than decorative: a body carrying `PRODUCT_DRAFT` with a
 * `restore_product` remedy **fails the parse**, which is the wire-side twin of the
 * guarantee that the same pairing does not compile at `gateRefusal`'s call site.
 */
function gateArm(code) {
    return z.object({
        ...problemMembers,
        code: z.literal(code),
        remedy: REMEDY_ARMS[GATE_REMEDY_ACTIONS[code]],
    });
}
/**
 * An RFC 9457 body for a §8.1 gate — every member a problem body has, plus `remedy`.
 *
 * **The eight codes are listed, and the list is not an unchecked second census.**
 * {@link GateTypeWidensToSchema} goes red the moment a `GateCode` is missing from it, and
 * {@link gateArm}'s `C extends GateCode` refuses a name the registry has never heard of —
 * so the two failure directions a hand-written list normally has are both closed by the
 * compiler. `z.discriminatedUnion` needs a tuple, which is why it is written out at all.
 */
export const gateRefusalBodySchema = z.discriminatedUnion("code", [
    gateArm("DOMAIN_NEVER_LIVE"),
    gateArm("PREFIX_NOT_VERIFIED"),
    gateArm("PREFIX_DISPUTED"),
    gateArm("XDIM_BELOW_MINIMUM"),
    gateArm("PRODUCT_DRAFT"),
    gateArm("PRODUCT_ARCHIVED"),
    gateArm("PRODUCT_TOMBSTONED"),
    gateArm("GTIN_CLAIMED"),
]);
/**
 * `artwork.preflight` — one row per product named, in the order they were named.
 *
 * **`refused` is the supertype, not {@link gateRefusalBodySchema}, and row 3 is why.**
 * §8.1's parser row answers with `INVALID_CHECK_DIGIT` — a plain problem body with no
 * remedy — so a schema that required one here would reject the one refusal on this
 * endpoint that carries a computable correction. The screen narrows on `code` and reads
 * `remedy` only where it is present, exactly as `ops/artwork.ts`'s own `GateVerdict` does.
 */
export const artworkPreflightRowSchema = z.object({
    productId: z.string(),
    gtin14: z.string().optional(),
    eligible: z.boolean(),
    refused: z
        .object({
        type: z.string(),
        title: z.string(),
        status: z.number(),
        detail: z.string(),
        code: z.string(),
        remedy: remedySchema.optional(),
    })
        .optional(),
    /** Exactly what the symbol would encode. Present on an eligible row only. */
    digitalLinkUri: z.string().optional(),
    /** §8.1's warn-only set. Present and empty rather than absent — §5. */
    warnings: z.array(warningBodySchema),
    /** §8.2's marks the file will carry. Present and empty; empty on a refused row. */
    watermarks: z.array(z.enum(ARTWORK_WATERMARKS)),
    /** §8.3's disclosure, on an eligible row whose URI would be a platform one. */
    disclosure: escapeHatchDisclosureSchema.optional(),
});
/**
 * `GET /v1/artwork/batches/{id}`, and the `batch` member of a `zip` preflight — U62.
 *
 * `expired` is derived from `expiresAt` rather than stored: a stored flag would need a
 * writer to keep it true, and `0033`'s CHECK set holds the four states a job can be in.
 * `downloadUrl` is a presigned GET and is null on every status but a live `ready`.
 */
export const artworkBatchStatusSchema = z.object({
    id: z.uuid(),
    status: z.enum(ARTWORK_BATCH_STATUSES),
    /** How many rows the gates admitted. The ZIP holds one entry each. */
    entries: z.number().int().nonnegative(),
    /** How many rows §8.1 withheld. `report.csv` names them, and exists only above zero. */
    withheld: z.number().int().nonnegative(),
    /** The archive's size once it exists. Null before, and on a failure. */
    bytes: z.number().int().nonnegative().nullable(),
    /** What the job hit. Null on every status but `failed`. */
    error: z.string().nullable(),
    expiresAt: z.string(),
    expired: z.boolean(),
    downloadUrl: z.string().nullable(),
});
export const artworkPreflightSchema = z.object({
    results: z.array(artworkPreflightRowSchema),
    /**
     * The ZIP job this request started, absent unless `zip` was set — U62.
     *
     * Absent rather than null, so a request that did not ask for a ZIP gets the answer it
     * got before U62, member for member. §8.1's sentence governs `results` and nothing
     * about `results` moved.
     */
    batch: artworkBatchStatusSchema.optional(),
});
/**
 * One `domains` row as the dashboard reads it — `/v1/domains` and `domains.list`.
 *
 * Timestamps are ISO strings rather than epoch ms, unlike `entry.ts`'s links: these are read
 * by a screen that formats them for a person ("Added 14 minutes ago"), never by the entry
 * builder, and an ISO string is what `Intl` takes.
 */
export const domainSummarySchema = z.object({
    id: z.uuid(),
    hostname: z.string(),
    kind: z.enum(DOMAIN_KINDS),
    status: z.enum(DOMAIN_STATUSES),
    /** Write-once and never cleared — the fact §8.1 gate 1 reads. */
    first_live_at: z.string().nullable(),
    last_checked_at: z.string().nullable(),
    /**
     * What the last DoH lookup returned. `null` means no lookup has run, which is a
     * different fact from a lookup that found nothing — the screen's error state renders
     * this as the "we found" half of its diff.
     */
    last_dns_answer: z.string().nullable(),
    /** `null` until a probe has run — a different fact from a probe that failed (`0016`). */
    last_probe_ok: z.boolean().nullable(),
    last_probe_at: z.string().nullable(),
    /**
     * Consecutive failed watch probes, `0` on any success. NOT nullable, unlike the two
     * above: a row nobody has watched has failed zero times, which is the true answer and
     * the one `>= SUSTAINED_PROBE_FAILURES` wants (`0027`).
     */
    consecutive_probe_failures: z.number().int(),
    /**
     * Distinct products whose artwork carries this hostname. State G's sentence, and the
     * reason removing a live domain is a speed bump — the codes are already in the field.
     */
    printed_sku_count: z.number().int(),
    created_at: z.string(),
});
/** The CNAME every tenant publishes, echoed by the API so no client spells it itself. */
export const dnsInstructionsSchema = z.object({
    type: z.literal("CNAME"),
    value: z.string(),
});
export const domainListSchema = z.object({
    domains: z.array(domainSummarySchema),
    dns: dnsInstructionsSchema,
});
export const domainCreatedSchema = z.object({
    id: z.uuid(),
    hostname: z.string(),
    kind: z.enum(DOMAIN_KINDS),
    status: z.enum(DOMAIN_STATUSES),
    dns: dnsInstructionsSchema,
    /** Present and empty rather than absent — §5. Carries `DOMAIN_APEX_STEERED`. */
    warnings: z.array(warningBodySchema),
});
export const domainRemovedSchema = z.object({
    id: z.uuid(),
    status: z.enum(DOMAIN_STATUSES),
    /** How many entries the removal re-emitted — `0016`'s org-wide obligation, counted. */
    entriesReEmitted: z.number().int(),
});
export const domainRecheckedSchema = z.object({
    id: z.uuid(),
    status: z.enum(DOMAIN_STATUSES),
    answer: z.string().nullable(),
});
//# sourceMappingURL=responses.js.map